import { z } from 'zod';

/**
 * Energy Balance contract (CLAUDE.md §11.1 `/balance`, §10 `GET /api/sites/:id/balance`,
 * §7.1 `energy_balance` table).
 *
 * Single source of truth for the measured-vs-modelled energy balance — imported by the
 * Worker route, its tests, and the React `/balance` screen (Sankey + closure KPIs +
 * balance-by-SEU table).
 *
 * Model (M05). Supply = grid intake (Eskom incomer) + on-site solar generation. The total
 * supply is allocated across Significant Energy Users (SEUs) by an engineered end-use share
 * (packages/config END_USE_SHARES) — the **modelled** value. Where an SEU has a submeter,
 * its **metered** value is the summed submeter kWh over the period. `closurePct` is the
 * fraction of total supply captured by submeters (a measurement-completeness metric that
 * rises as more submeters come online).
 *
 * TODO(spec): the modelled share is a fixed engineered split until per-SEU baseline
 * regression lands (M07) — at that point `modelled` becomes the baseline-predicted load and
 * the energy balance becomes a true M&V instrument. Recorded so the swap is a known seam.
 */

/** Energy values are reported in GWh (CLAUDE.md §7.1 energy_balance columns). */

/** Role of a node in the Sankey flow: a supply source, the load hub, or an end-use SEU. */
export const SankeyNodeKindSchema = z.enum(['source', 'hub', 'seu']);
export type SankeyNodeKind = z.infer<typeof SankeyNodeKindSchema>;

/** One node in the energy-balance Sankey (rendered by the custom SankeyFlow SVG, §2). */
export const SankeyNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  kind: SankeyNodeKindSchema,
  /** Total throughput of this node (GWh) — drives node height in the Sankey. */
  valueGwh: z.number(),
});
export type SankeyNode = z.infer<typeof SankeyNodeSchema>;

/** One directed link in the Sankey (source→hub or hub→seu). Width ∝ valueGwh. */
export const SankeyLinkSchema = z.object({
  source: z.string(),
  target: z.string(),
  valueGwh: z.number(),
});
export type SankeyLink = z.infer<typeof SankeyLinkSchema>;

/** One row of the balance-by-SEU table: metered vs modelled with the signed variance. */
export const SeuBalanceRowSchema = z.object({
  seuId: z.string(),
  name: z.string(),
  category: z.string(),
  /** Summed submeter energy for this SEU over the period (GWh); 0 when unmetered. */
  meteredGwh: z.number(),
  /** Engineered (M07: baseline-predicted) modelled energy for this SEU (GWh). */
  modelledGwh: z.number(),
  /** metered − modelled (GWh); positive = metered above model. */
  varianceGwh: z.number(),
  /** variance as a % of modelled; null when modelled is zero. */
  variancePct: z.number().nullable(),
  /** False until this SEU is submetered — UI flags modelled-only rows. */
  metered: z.boolean(),
});
export type SeuBalanceRow = z.infer<typeof SeuBalanceRowSchema>;

export const BalanceResponseSchema = z.object({
  siteId: z.string(),
  /** Period key, YYYY-MM (CLAUDE.md §7.1). */
  period: z.string(),
  generatedAt: z.string().datetime(),
  /** Grid (Eskom incomer) energy for the period (GWh). */
  intakeGwh: z.number(),
  /** On-site solar generation for the period (GWh). */
  solarGwh: z.number(),
  /** intake + solar — total energy available to loads (GWh). */
  supplyGwh: z.number(),
  /** Σ modelled SEU energy (GWh) — should approximate supply by construction. */
  modelledGwh: z.number(),
  /** Σ metered SEU energy (GWh) — what submeters actually captured. */
  meteredGwh: z.number(),
  /** 100 × meteredGwh / supplyGwh — measurement completeness (%). 0 when no supply. */
  closurePct: z.number(),
  nodes: z.array(SankeyNodeSchema),
  links: z.array(SankeyLinkSchema),
  rows: z.array(SeuBalanceRowSchema),
  /** True until real metered data drives the balance — UI shows the illustrative chip. */
  illustrative: z.boolean(),
});
export type BalanceResponse = z.infer<typeof BalanceResponseSchema>;
